/**
 * Navigation for the signed-in user: every module's nav items, filtered by permission and
 * enabled module (RULES §UI-6), ordered by the persona (`/api/me.persona.nav`, IA §6).
 */
import { useMemo } from 'react';
import type { Me } from '@/lib/api/types';
import { can } from '@/lib/can';
import { moduleEnabled, moduleKnown, useMe } from '@/lib/me';
import { allNavItems } from '@/modules/registry';
import type { NavChild, NavItem } from '@/modules/types';

/**
 * Destinations whose server module does not exist yet are "planned". In development they are
 * shown (marked "Soon") so the shell can be reviewed; production builds hide them.
 */
export const NAV_PREVIEW = import.meta.env.VITE_NAV_PREVIEW ? import.meta.env.VITE_NAV_PREVIEW === 'true' : import.meta.env.DEV;

export interface VisibleNavItem extends NavItem {
  planned: boolean;
  children: NavChild[];
}

export function visibleNav(me: Me, items: NavItem[], preview = NAV_PREVIEW): { primary: VisibleNavItem[]; more: VisibleNavItem[] } {
  const visible: VisibleNavItem[] = [];
  for (const item of items) {
    if (item.when && !item.when(me)) continue;
    const planned = !!item.module && !moduleKnown(me, item.module);
    if (planned) {
      if (preview) visible.push({ ...item, planned, children: [] });
      continue;
    }
    if (!can(me, item.permission) || !moduleEnabled(me, item.module)) continue;
    const children = (item.children ?? []).filter((c) => can(me, c.permission));
    // The destination's own page is one of its children the user may not open (Fleet opens
    // Vehicles): land on the first child they can open instead of a "no access" page.
    const hidden = item.children?.some((c) => c.to === item.to) && !children.some((c) => c.to === item.to);
    visible.push({ ...item, to: hidden && children[0] ? children[0].to : item.to, planned, children });
  }
  const byKey = new Map<string, VisibleNavItem>();
  for (const v of visible) {
    byKey.set(v.key, v);
    v.aliases?.forEach((a) => byKey.has(a) || byKey.set(a, v));
  }
  const primary: VisibleNavItem[] = [];
  for (const key of me.persona.nav) {
    const item = byKey.get(key);
    if (item && !primary.includes(item)) primary.push(item);
  }
  const more = visible.filter((v) => !primary.includes(v)).sort((a, b) => a.order - b.order);
  // A persona without a nav list (API clients) still gets the default order.
  return primary.length ? { primary, more } : { primary: more, more: [] };
}

export function useNav(): { primary: VisibleNavItem[]; more: VisibleNavItem[]; all: VisibleNavItem[] } {
  const me = useMe();
  return useMemo(() => {
    const nav = visibleNav(me, allNavItems());
    return { ...nav, all: [...nav.primary, ...nav.more] };
  }, [me]);
}

type Matchable = { to: string; match?: string[] };

/** How precisely `pathname` is inside this destination: its longest matching prefix (0 = not inside). */
export function matchLength(item: Matchable, pathname: string): number {
  const prefixes = item.match ?? [item.to];
  let best = 0;
  for (const p of prefixes) {
    const inside = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`);
    if (inside) best = Math.max(best, p.length);
  }
  return best;
}

/**
 * Is `pathname` inside this destination — and not more precisely inside one of its siblings
 * (`among`)? Fleet matches `/fleet` and Assets `/fleet/assets`: on an asset only Assets is active;
 * Activity's Timeline matches `/activity`, Trips `/activity/trips`: on a trip only Trips is.
 */
export function isActive(item: Matchable, pathname: string, among: readonly Matchable[] = []): boolean {
  const own = matchLength(item, pathname);
  return own > 0 && !among.some((o) => o !== item && matchLength(o, pathname) > own);
}
